import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Input, Select, TextArea, Consent, Honeypot, validateContact, CONTACT_METHODS } from "@/components/site/Form";

export function SuccessNote({ title, children, testid }) {
  return (
    <div className="flex gap-4 border border-forest/40 bg-forest/5 p-6" role="status" data-testid={testid}>
      <CheckCircle2 className="h-6 w-6 shrink-0 text-forest" strokeWidth={1.5} />
      <div><p className="font-serif text-2xl text-forest">{title}</p><p className="mt-1 text-sm text-charcoal/80">{children}</p></div>
    </div>
  );
}

export function useLeadForm(initial) {
  const [f, setF] = useState({ name: "", email: "", phone: "", preferred_contact: "Telephone", message: "", consent: false, website: "", ...initial });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const bind = (k) => ({ value: f[k], onChange: (e) => setF({ ...f, [k]: e.target.value }), error: errors[k] });
  const submit = async (payload, extraErrors = {}) => {
    const e = { ...validateContact(f), ...extraErrors };
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    try {
      await api.post("/enquiries", { ...f, ...payload });
      setDone(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally { setBusy(false); }
  };
  return { f, setF, errors, bind, busy, done, submit };
}

export function EnquiryForm({ vehicle }) {
  const title = `${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.derivative}`;
  const { f, setF, errors, bind, busy, done, submit } = useLeadForm({ part_exchange: false });
  if (done) return <SuccessNote title="Thank you — enquiry received." testid="enquiry-success">We will be in touch shortly about the {title}.</SuccessNote>;
  return (
    <form noValidate className="relative space-y-5" data-testid="enquiry-form"
      onSubmit={(e) => { e.preventDefault(); submit({ enquiry_type: "Vehicle enquiry", lead_source: "Website - vehicle page", vehicle_id: vehicle.id, vehicle_title: title }); }}>
      <Honeypot value={f.website} onChange={(v) => setF({ ...f, website: v })} />
      <div className="border border-stone bg-[#FBF9F4] px-4 py-3 text-sm"><span className="text-charcoal/70">Vehicle: </span><strong data-testid="enquiry-vehicle">{title}</strong></div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Input id="enq-name" label="Name" required autoComplete="name" {...bind("name")} />
        <Input id="enq-email" label="Email" type="email" required autoComplete="email" {...bind("email")} />
        <Input id="enq-phone" label="Telephone" type="tel" required autoComplete="tel" {...bind("phone")} />
        <Select id="enq-method" label="Preferred contact method" options={CONTACT_METHODS} {...bind("preferred_contact")} />
      </div>
      <TextArea id="enq-message" label="Message" placeholder="Anything you would like to know, or a time you'd like to view." {...bind("message")} />
      <label className="flex items-center gap-3 text-sm"><input type="checkbox" className="h-4 w-4 accent-[#16352C]" checked={f.part_exchange} onChange={(e) => setF({ ...f, part_exchange: e.target.checked })} data-testid="checkbox-part-exchange" />I have a vehicle to part exchange.</label>
      <Consent id="enq-consent" checked={f.consent} onChange={(v) => setF({ ...f, consent: v })} error={errors.consent} />
      <button type="submit" disabled={busy} className="btn btn-primary w-full sm:w-auto" data-testid="enquiry-submit">{busy ? "Sending…" : "Enquire"}</button>
    </form>
  );
}

export function PartExchangeForm({ vehicle }) {
  const { f, setF, errors, bind, busy, done, submit } = useLeadForm({ registration: "", mileage: "", condition: "", service_history: "", outstanding_finance: "" });
  const [regErr, setRegErr] = useState("");
  if (done) return <SuccessNote title="Thank you — valuation requested." testid="px-success">We will review your vehicle details and come back to you with an appraisal.</SuccessNote>;
  return (
    <form noValidate className="relative space-y-5" data-testid="px-form"
      onSubmit={(e) => {
        e.preventDefault();
        const re = f.registration.trim() ? "" : "Please enter your registration";
        setRegErr(re);
        submit({
          enquiry_type: "Part exchange", lead_source: "Website - vehicle page part exchange", part_exchange: true,
          vehicle_id: vehicle.id, vehicle_title: `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
          part_exchange_details: { registration: f.registration, mileage: f.mileage, condition: f.condition, service_history: f.service_history, outstanding_finance: f.outstanding_finance },
        }, re ? { registration: re } : {});
      }}>
      <Honeypot value={f.website} onChange={(v) => setF({ ...f, website: v })} />
      <div className="grid gap-5 sm:grid-cols-2">
        <Input id="px-reg" label="Registration" required value={f.registration} onChange={(e) => setF({ ...f, registration: e.target.value.toUpperCase() })} error={regErr || errors.registration} hint="Make and model will auto-fill once vehicle lookup is connected." />
        <Input id="px-mileage" label="Mileage" inputMode="numeric" {...bind("mileage")} />
        <Select id="px-condition" label="Condition" options={["Excellent", "Good", "Fair", "Needs work"]} {...bind("condition")} />
        <Select id="px-history" label="Service history" options={["Full main dealer", "Full specialist", "Part", "None"]} {...bind("service_history")} />
        <Select id="px-finance" label="Outstanding finance" options={["No", "Yes", "Not sure"]} {...bind("outstanding_finance")} />
        <Input id="px-name" label="Name" required {...bind("name")} />
        <Input id="px-email" label="Email" type="email" required {...bind("email")} />
        <Input id="px-phone" label="Telephone" type="tel" required {...bind("phone")} />
      </div>
      <Consent id="px-consent" checked={f.consent} onChange={(v) => setF({ ...f, consent: v })} error={errors.consent} />
      <button type="submit" disabled={busy} className="btn btn-primary" data-testid="px-submit">{busy ? "Sending…" : "Request a valuation"}</button>
    </form>
  );
}
